import {
  Stack,
  TextField,
  Autocomplete,
} from '@mui/material';
import { type FC, useMemo } from 'react';
import { Controller} from 'react-hook-form';

import { useMutateCreateEmergencyContact, useMutateUpdateEmergencyContact } from '../../api/emergency-contacts.api.js';
import { type EmergencyContact } from '../../models/index.js';
import { Events, track } from '../../utils/index.js';
import AutocompleteOptions from '../AutocompleteOptions.js';
import FormDialog from '../Forms/FormDialog.js';
import { type EmergencyContactFormValues } from '../Forms/formTypes.js';
import { emergencyContactFormSchema } from '../Forms/validations.js';

export const RELATIONSHIP_OPTIONS: Array<string> = [
  'Power of attorney',
  'Child',
  'Grandchild',
  'Sibling',
  'Neighbour',
  'Caregiver',
  'Nurse',
  'Unknown',
];


const defaultValues: EmergencyContactFormValues = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  relationship: 'Unknown',
};

interface EmergencyContactFormProps {
  open: boolean;
  onClose: () => void;
  contact?: EmergencyContact;
  patientId: string;
  refetch: () => void;
}

const EmergencyContactForm: FC<EmergencyContactFormProps> = ({ open, contact, patientId, refetch, onClose }) => {
  const createMutation = useMutateCreateEmergencyContact();
  const updateMutation = useMutateUpdateEmergencyContact();
  const formattedData: EmergencyContactFormValues = useMemo(() => {
    if (contact) {
      return {
        firstName: contact.firstName,
        lastName: contact.lastName,
        phoneNumber: contact.phoneNumber,
        relationship: contact.relationship,
      };
    }
    return defaultValues;
  }, [contact]);

  return (
    <FormDialog
      open={open}
      name={'emergency-contact'}
      onClose={onClose}
      data={formattedData}
      schema={emergencyContactFormSchema}
      isEditing={!!contact}
      onSubmit={async (data, isEditing) => {
        if (isEditing && contact) {
          await updateMutation.mutateAsync({
            ...data,
            id: contact.id,
          });
          track(Events.EmergencyContactUpdate, {
            emergencyContactId: contact?.id,
            patientId,
          });
        } else {
          const response = await createMutation.mutateAsync({
            ...data,
            priority: 0,
            patientId,
          });
          track(Events.EmergencyContactCreation, {
            emergencyContactId: response.id,
            patientId,
          });
        }
      }}
      refetch={refetch}
    >
      {(control, errors) => (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Controller
            name={'firstName'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                InputLabelProps={{ required: true }}
                error={!!errors?.firstName}
                helperText={errors?.firstName?.message || ''}
                label={'First name'}
              />
            )}
          />
          <Controller
            name={'lastName'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                InputLabelProps={{ required: true }}
                error={!!errors?.lastName}
                helperText={errors?.lastName?.message || ''}
                label={'Last name'}
              />
            )}
          />
          <Controller
            name={'phoneNumber'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                InputLabelProps={{ required: true }}
                type={'tel'}
                error={!!errors?.phoneNumber}
                helperText={errors?.phoneNumber?.message || ''}
                label={'Phone number'}
              />
            )}
          />
          <Controller
            name={'relationship'}
            control={control}
            render={({ field }) => {
              return (
                <Autocomplete
                  {...field}
                  value={field.value}
                  onChange={(_: any, value: string | null) => field.onChange(value)}
                  id={'relationship-select'}
                  options={RELATIONSHIP_OPTIONS}
                  isOptionEqualToValue={(option, value) => option === value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      InputLabelProps={{ required: true }}
                      error={!!errors?.relationship}
                      helperText={errors?.relationship?.message || ''}
                      label={'Relationship to patient'}
                    />
                  )}
                  renderOption={({ key, ...props }, option, { inputValue }) => (
                    <AutocompleteOptions option={option} value={inputValue} key={key} {...props} />
                  )}
                />
              );
            }}
          />
        </Stack>
      )}
    </FormDialog>
  );
};

export default EmergencyContactForm;
